import { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { IFilterResult } from '@components/filters/filterLib';
import { groupFilterValues, homeLocationFilterValues } from '@components/filters/devicesFilterLib';
import { useGenericFilterOptions } from '../../../../../components/data-table/filters/useGenericFilterOptions';
import { getGroups, getHomeLocations, getManufacturers, getModels, IGroup, IGroups, IHomeLocation } from '../../../../../services/config/config';

export type CorePerformanceDevicesFilterData = [IGroups, string[], string[], IHomeLocation[]];

function getInitialAvailableFilters(): IFilterResult {
  return {
    manufacturers: [],
    models: [],
    groups: [],
    homeLocations: []
  };
}

export const useCorePerformanceDevicesFilterOptions = () => {
  const { t } = useTranslation(['translation', 'filters']);

  const dataProcessor = useMemo(() => {
    return (data: CorePerformanceDevicesFilterData): IFilterResult => {
      const translatedGroupFilterValues = groupFilterValues.map((val) => ({
        ...val,
        value: t(val.value as any, { ns: 'translation' })
      }));

      const translatedHomeLocationFilterValues = homeLocationFilterValues.map((val) => ({
        ...val,
        value: t(val.value as any, { ns: 'translation' })
      }));

      const [groupsRaw, models, manufacturers, homeLocationsRaw] = data;

      const groups = [
        ...translatedGroupFilterValues,
        ...(groupsRaw?.groups?.nodes ?? []).map((o: IGroup) => o.name)
      ];

      const homeLocations = [
        ...translatedHomeLocationFilterValues,
        ...(homeLocationsRaw ?? []).map((o) => ({ id: o.id, value: o.name }))
      ];

      return {
        groups,
        models,
        manufacturers,
        homeLocations
      };
    };
  }, [t]);

  const dataFetchers = useMemo(() => [getGroups, getModels, getManufacturers, getHomeLocations], []);

  const { availableFilters, filtersLoaded } = useGenericFilterOptions({
    hiddenFilters: [],
    dataFetchers,
    dataProcessor,
    getInitialFilters: getInitialAvailableFilters
  });

  return {
    availableFilters,
    filtersLoaded
  };
};
